import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';

const DEFAULT_PAGE_SIZE = 24;

const FILTER_URL_KEYS = ['age_lte', 'nb_joueurs', 'temps_max', 'pvc_min', 'pvc_max', 'public_cible', 'type_jeu', 'tags_produit_ids', 'partner_id', 'editeur'];
const ARRAY_FILTERS = ['public_cible', 'type_jeu', 'tags_produit_ids'];

function parseFiltersFromURL(searchParams) {
  const filters = {};
  FILTER_URL_KEYS.forEach(key => {
    const val = searchParams.get(key);
    if (!val) return;
    if (ARRAY_FILTERS.includes(key)) {
      filters[key] = val.split(',').filter(Boolean);
    } else if (['age_lte', 'nb_joueurs', 'temps_max'].includes(key)) {
      filters[key] = parseInt(val);
    } else if (['pvc_min', 'pvc_max'].includes(key)) {
      filters[key] = parseFloat(val);
    } else {
      filters[key] = val;
    }
  });
  return filters;
}

/**
 * useProductCatalog — Hook central catalogue Retailer (Phase 3.4.2.1 — fix loading race condition)
 */
export default function useProductCatalog({ contextFilters = {}, initialSort = '', pageSize = DEFAULT_PAGE_SIZE, syncURL = false, apiEndpoint = '/products' } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(() => syncURL ? parseFiltersFromURL(searchParams) : {});
  const [sort, setSort] = useState(() => (syncURL && searchParams.get('sort')) || initialSort);
  const [page, setPage] = useState(() => {
    if (syncURL) {
      const p = parseInt(searchParams.get('page'));
      return isNaN(p) ? 0 : p;
    }
    return 0;
  });

  const abortRef = useRef(null);
  const fetchIdRef = useRef(0); // Track fetch generation to avoid stale updates

  // Sync filters to URL (only when syncURL=true)
  useEffect(() => {
    if (!syncURL) return;
    const urlParams = new URLSearchParams(searchParams);
    FILTER_URL_KEYS.forEach(k => urlParams.delete(k));
    urlParams.delete('sort');
    urlParams.delete('page');
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return;
      urlParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    if (sort) urlParams.set('sort', sort);
    if (page > 0) urlParams.set('page', String(page));
    setSearchParams(urlParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, page, syncURL]);

  // Stabilize contextFilters to avoid infinite re-renders
  const contextFiltersKey = useMemo(() => JSON.stringify(contextFilters), [contextFilters]);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    const ctx = JSON.parse(contextFiltersKey);
    if (apiEndpoint === '/products' || apiEndpoint.startsWith('/products')) {
      Object.entries(ctx).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      });
    }
    const f = filters;
    if (f.age_lte) params.set('age_lte', f.age_lte);
    if (f.nb_joueurs) params.set('nb_joueurs', f.nb_joueurs);
    if (f.temps_max) params.set('temps_max', f.temps_max);
    if (f.pvc_min) params.set('pvc_min', f.pvc_min);
    if (f.pvc_max) params.set('pvc_max', f.pvc_max);
    if (f.public_cible && f.public_cible.length > 0) params.set('public_cible', f.public_cible.join(','));
    if (f.type_jeu && f.type_jeu.length > 0) params.set('type_jeu', f.type_jeu[0]);
    if (f.tags_produit_ids && f.tags_produit_ids.length > 0) {
      params.set('tags_produit_ids', f.tags_produit_ids.join(','));
      params.set('tags_produit_mode', 'and');
    }
    if (f.partner_id) params.set('partner_id', f.partner_id);
    if (f.editeur) params.set('editeur', f.editeur);
    if (sort) params.set('sort', sort);
    params.set('limit', pageSize);
    params.set('offset', page * pageSize);
    return params;
  }, [contextFiltersKey, filters, sort, page, pageSize, apiEndpoint]);

  const fetchProducts = useCallback(async () => {
    // Abort previous request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    
    // Track this fetch generation
    const thisFetchId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const res = await api.get(`${apiEndpoint}?${params.toString()}`, { signal: controller.signal });
      
      // Only update state if this is still the latest fetch (not stale)
      if (thisFetchId !== fetchIdRef.current) return;
      
      const data = res.data;
      if (data && Array.isArray(data.products)) {
        setProducts(data.products);
        setTotal(data.total || data.products.length);
      } else if (Array.isArray(data)) {
        setProducts(data);
        setTotal(data.length);
      } else {
        setProducts([]);
        setTotal(0);
      }
    } catch (err) {
      // Silently ignore aborted requests
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      if (thisFetchId !== fetchIdRef.current) return;
      console.error('[useProductCatalog] Fetch error:', err);
      setError('Impossible de charger le catalogue. Veuillez réessayer.');
      setProducts([]);
      setTotal(0);
    } finally {
      // Only clear loading if this is still the latest fetch
      if (thisFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [buildParams, apiEndpoint]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const setFilterValue = useCallback((key, value) => {
    setFilters(prev => {
      const next = { ...prev };
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPage(0);
  }, []);

  const clearFilter = useCallback((key) => {
    setFilters(prev => { const next = { ...prev }; delete next[key]; return next; });
    setPage(0);
  }, []);

  const clearAllFilters = useCallback(() => { setFilters({}); setPage(0); }, []);
  const handleSortChange = useCallback((newSort) => { setSort(newSort); setPage(0); }, []);

  return {
    products, total, loading, error, filters,
    setFilter: setFilterValue, clearFilter, clearAllFilters,
    sort, setSort: handleSortChange,
    page, setPage,
    totalPages: Math.ceil(total / pageSize),
    pageSize,
    refresh: fetchProducts,
  };
}
